import { TransactionReceipt as ViemTransactionReceipt, WalletClient } from "viem";
import type { WalletClient as ViemWalletClient } from "viem";

import { sendTransaction as _sendTransaction, switchChain } from "@shared/utils/viemWalletUtils";
import type { TransactionReceipt } from "@shared/types";

import type {
    SignMessage,
    SendTransaction,
    SignTypedData,
    EvmWalletRole,
    UserWalletFunderDisplay,
} from "@halliday-sdk/payments";

/**
 * `viem` is installed both at the monorepo root and nested under `payments/node_modules`
 * at the same version. This file resolves the payments copy while `@shared/utils` resolves
 * the root copy, and viem's self-referential `Client.extend` generic makes the two
 * `WalletClient` types non-assignable across copies even though they are identical. Bridge
 * them with the shared helpers' own expected type when crossing that boundary.
 */
type SharedWalletClient = Parameters<typeof switchChain>[1];

/**
 * Get the transaction receipt from the ethers transaction receipt.
 *
 * @param {ViemTransactionReceipt} txReceipt The viem transaction receipt.
 * @returns {TransactionReceipt} The transaction receipt.
 */
const _getTransactionReceipt = (txReceipt: ViemTransactionReceipt): TransactionReceipt => {
    return {
        transactionHash: txReceipt.transactionHash,
        blockHash: txReceipt.blockHash,
        blockNumber: Number(txReceipt.blockNumber),
        from: txReceipt.from,
        to: txReceipt.to || undefined,
        rawReceipt: txReceipt,
    };
};

export function connectWalletClient(
    getClientWallet: () => ViemWalletClient,
    ownerFunderDisplay: "HIDE",
): Omit<EvmWalletRole, "sendTransaction">;

export function connectWalletClient(
    getClientWallet: () => ViemWalletClient,
    ownerFunderDisplay?: "SHOW",
): EvmWalletRole;

/**
 * Connect to a wallet.
 *
 * @param {function} getClientWallet {{@link WalletClient}} Function to get the viem wallet client.
 * @returns {function} ``getAddress`` {function} Function that returns the addresse of the wallet.
 * @returns {function} ``signMessage`` {{@link SignMessage}} Function that signs a message.
 * @returns {function} ``signTypedData`` {{@link SignTypedData}} Function that signs typed data.
 * @returns {function} ``sendTransaction`` {{@link SendTransaction}} Function that sends an EVM transaction.
 * @returns {string} ``walletType`` The literal `"EVM"`.
 */
export function connectWalletClient(
    getClientWallet: () => ViemWalletClient,
    ownerFunderDisplay?: UserWalletFunderDisplay,
) {
    // return the first address of the wallet
    const getAddress = async () => {
        const walletClient = getClientWallet();
        const addresses = await walletClient.getAddresses();
        const address = addresses[0];
        if (address === undefined) throw new Error("No address found in wallet client");
        return address;
    };

    const signMessage: SignMessage = async ({ message, ownerAddress }) => {
        const walletClient = getClientWallet();
        const signature = await walletClient.signMessage({
            account: ownerAddress as `0x${string}`,
            message,
        });
        return signature;
    };

    const sendTransaction: SendTransaction = async (transaction, chainConfig) => {
        let walletClient = getClientWallet();

        const address = await getAddress();
        transaction.from = address;

        const currentChain = await walletClient.getChainId();

        if (BigInt(chainConfig.chain_id) !== BigInt(currentChain)) {
            await switchChain(Number(chainConfig.chain_id), walletClient as SharedWalletClient, chainConfig);
            walletClient = getClientWallet();
        }

        const viemTxReceipt = await _sendTransaction({
            walletClient: walletClient as SharedWalletClient,
            transaction,
            chainConfig,
        });
        return _getTransactionReceipt(viemTxReceipt);
    };

    const signTypedData: SignTypedData = async ({ typedData, ownerAddress, chainId }) => {
        let walletClient = getClientWallet();

        if (chainId !== undefined) {
            const currentChain = await walletClient.getChainId();
            if (BigInt(chainId) !== BigInt(currentChain)) {
                await switchChain(chainId, walletClient as SharedWalletClient);
                walletClient = getClientWallet();
            }
        }

        const typedDataObject = JSON.parse(typedData);
        const signature = await walletClient.signTypedData({
            account: ownerAddress as `0x${string}`,
            ...typedDataObject,
        });
        return signature;
    };

    if (ownerFunderDisplay === "HIDE") {
        return {
            getAddress,
            signMessage,
            signTypedData,
            walletType: "EVM" as const,
        };
    }

    return {
        getAddress,
        signMessage,
        sendTransaction,
        signTypedData,
        walletType: "EVM" as const,
    } as EvmWalletRole;
}
